<template>
    <v-dialog
        :value="isDialogOpen"
        max-width="700px"
        transition="slide-y-transition"
        @keydown.esc="handleCloseDialog"
        @click:outside="handleCloseDialog"
    >
        <v-card>
            <v-card-title>
                <h1 class="text-subtitle-1">
                    Wybierz wadliwy montaż w zleceniu
                </h1>
            </v-card-title>

            <v-card-text class="text-center">
                <v-container fluid class="pa-0">
                    <v-row no-gutters class="px-4 pb-2 align-center">
                        <v-col cols="12" class="d-flex align-center justify-end">
                            <v-checkbox
                                v-model="selectAll"
                                label="Zaznacz wszystkie montaże w zleceniu"
                                :disabled="!platesData.length"
                                hide-details
                                class="ma-0 pa-0"
                                @change="toggleSelectAll"
                            />
                        </v-col>
                    </v-row>

                    <v-simple-table dense>
                        <thead>
                            <tr>
                                <th class="text-left" style="width:35%">
                                    Numer montażu
                                </th>
                                <th class="text-left" style="width:15%">
                                    Nakład
                                </th>
                                <th class="text-left" style="width:25%">
                                    Wadliwy nakład
                                </th>
                                <th class="text-left" style="width:25%">
                                    Wadliwy nakład w %
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-if="!platesData.length">
                                <td colspan="4" class="text-center grey--text">
                                    Brak montaży do wyświetlenia
                                </td>
                            </tr>
                            <tr v-for="plate in platesData" :key="plate.id">
                                <td>
                                    <div class="d-flex align-center">
                                        <v-checkbox
                                            v-model="plate.selected"
                                            class="ma-0 pa-0 mr-2"
                                            hide-details
                                            @change="onRowSelectChange(plate)"
                                        />
                                        <span class="font-weight-medium">Montaż: {{ plate.plateNumber }}</span>
                                    </div>
                                </td>

                                <td>{{ plate.amount }}</td>

                                <td style="max-width:140px">
                                    <v-text-field
                                        v-model.number="plate.defectiveAmount"
                                        type="number"
                                        dense
                                        :disabled="!plate.selected"
                                        :min="0"
                                        :error="isDefectiveGreater(plate) || getDefectiveError(plate).length > 0"
                                        :error-messages="getDefectiveError(plate)"
                                        :hide-details="getDefectiveError(plate).length === 0"
                                        @input="onDefectiveInput(plate)"
                                        @blur="onDefectiveBlur(plate)"
                                    />
                                </td>

                                <td>
                                    <span v-if="plate.defectiveAmountPercent !== null">{{ formatPercent(plate.defectiveAmountPercent) }} %</span>
                                </td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-container>
            </v-card-text>

            <v-divider />

            <v-card-actions>
                <v-btn
                    color="error"
                    text
                    @click="handleCloseDialog"
                >
                    Anuluj
                </v-btn>

                <v-spacer />

                <v-btn
                    color="success"
                    text
                    :disabled="saveDisabled"
                    @click="handleSaveDefects"
                >
                    Zapisz
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
/* eslint-disable vue/no-mutating-props */
export default {
    name: 'AddJobDefectDialog',

    props: {
        isDialogOpen: {
            type: Boolean
        },
        reportItem: {
            type: Object,
            default: null
        },
        complaint: {
            type: Object,
            default: null
        }
    },

    data() {
        return {
            platesData: [],
            selectAll: false
        }
    },

    computed: {
        saveDisabled() {
            return this.platesData.some(p => Number(p.defectiveAmount) > p.amount)
        }
    },

    watch: {
        isDialogOpen(val) {
            if (val) this.initializePlates()
        }
    },

    methods: {
        formatPercent(value) {
            if (value === null || value === undefined || value === '') return ''
            const num = Number(value)

            if (Number.isNaN(num)) return ''

            return num.toFixed(1)
        },

        isDefectiveGreater(plate) {
            if (plate.defectiveAmount === '' || plate.defectiveAmount === null) return false

            return Number(plate.defectiveAmount) > plate.amount
        },

        getDefectiveError(plate) {
            if (plate.defectiveAmount === '' || plate.defectiveAmount === null) return []
            const val = Number(plate.defectiveAmount)

            if (val < 0 || val > plate.amount) {
                return ['Podaj wartość w zakresie 0 - ' + plate.amount]
            }

            return []
        },

        initializePlates() {
            const plates = this.reportItem?.reportItemPlates || []
            const currentJobNumber = this.reportItem?.primaryNumber || this.reportItem?.jobNumber || this.complaint?.jobNumber || ''
            const defects = Array.isArray(this.complaint?.defects) ? this.complaint.defects : []
            const hasAnyJobNumber = defects.some(d => d.jobNumber)
            const defectsByPlateNumber = new Map(
                defects
                    .filter(d => (!hasAnyJobNumber && !d.jobNumber) || d.jobNumber === currentJobNumber)
                    .map(d => [String(d.plateNumber).trim(), d])
            )

            // Remove duplicates for sides A,B,AB - pick the one with the highest amount - if there are multiple with the same amount, pick the first one
            const deduplicatedPlates = Array.from(
                plates.reduce((map, plate) => {
                    const key = String(plate.plateNumber).trim()
                    const existing = map.get(key)

                    if (!existing || plate.amount > existing.amount) {
                        map.set(key, plate)
                    }

                    return map
                }, new Map()).values()
            )

            this.platesData = deduplicatedPlates.map(plate => {
                const key = String(plate.plateNumber).trim()
                const matched = defectsByPlateNumber.get(key)

                return {
                    id: plate.id,
                    plateNumber: plate.plateNumber,
                    amount: plate.amount,
                    defectiveAmount: matched ? matched.defectiveAmount : 0,
                    defectiveAmountPercent: matched ? Number(matched.defectiveAmountPercent).toFixed(1) : null,
                    selected: (matched?.defectiveAmount ?? 0) > 0,
                    jobNumber: currentJobNumber,
                    orderNumber: null
                }
            })
            this.selectAll = this.platesData.length > 0 && this.platesData.every(p => p.selected)
        },

        toggleSelectAll() {
            this.platesData.forEach(plate => {
                plate.selected = this.selectAll
                if (!this.selectAll) {
                    plate.defectiveAmount = 0
                    plate.defectiveAmountPercent = null
                }
            })
        },

        onRowSelectChange(plate) {
            if (!plate.selected) {
                plate.defectiveAmount = 0
                plate.defectiveAmountPercent = null
                this.selectAll = false
            } else if (this.platesData.every(p => p.selected)) {
                this.selectAll = true
            }
        },

        onDefectiveInput(plate) {
            if (plate.defectiveAmount === '' || plate.defectiveAmount === null) {
                plate.defectiveAmountPercent = null

                return
            }
            if (plate.defectiveAmount < 0) plate.defectiveAmount = 0
            if (Number(plate.defectiveAmount) > plate.amount) {
                plate.defectiveAmountPercent = null
                if (!plate.selected && plate.defectiveAmount > 0) plate.selected = true
                this.selectAll = this.platesData.every(p => p.selected)

                return
            }
            const percent = plate.amount ? (plate.defectiveAmount / plate.amount) * 100 : 0

            plate.defectiveAmountPercent = percent.toFixed(1)
            if (!plate.selected && plate.defectiveAmount > 0) plate.selected = true
            if (this.platesData.every(p => p.selected)) this.selectAll = true
        },

        onDefectiveBlur(plate) {
            if (!plate.defectiveAmount) {
                plate.defectiveAmount = 0
                plate.defectiveAmountPercent = null
            }
        },

        handleCloseDialog() {
            this.$emit('closeDialog')
        },

        handleSaveDefects() {
            const currentJobNumber = this.reportItem?.primaryNumber || this.reportItem?.jobNumber || this.complaint?.jobNumber || ''
            const newDefects = this.platesData
                .filter(plate => plate.selected && plate.defectiveAmount > 0 && plate.defectiveAmount <= plate.amount)
                .map(plate => ({
                    plateId: plate.id,
                    plateNumber: plate.plateNumber,
                    amount: plate.amount,
                    defectiveAmount: plate.defectiveAmount,
                    defectiveAmountPercent: plate.defectiveAmountPercent,
                    jobNumber: currentJobNumber,
                    orderNumber: null
                }))

            if (!this.complaint) {
                this.$emit('saveDefects', newDefects)
                this.handleCloseDialog()

                return
            }
            if (!Array.isArray(this.complaint.defects)) this.$set(this.complaint, 'defects', [])
            const preserved = this.complaint.defects.filter(d => d.jobNumber && d.jobNumber !== currentJobNumber)

            this.$set(this.complaint, 'defects', [...preserved, ...newDefects])
            this.$emit('saveDefects', this.complaint.defects)
            this.handleCloseDialog()
        }
    }
}
</script>
